import { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid2 from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { styled, keyframes } from '@mui/material/styles';
import { Visibility, VisibilityOff, Lock, Email } from '@mui/icons-material';
import logo from './../../../assets/images/logo.png';
import { resetPassword } from '../store/auth.actions';
import { resetError, resetLoading } from '../store/auth.selectors';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  BoxContainer,
  AuthTextField,
  PrimaryButton,
  LogoContainer,
  StyledLogo,
  FormContainer,
} from '../../../utils/common/styles/commonStyles';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

// Styled Components
const PageContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  padding: theme.spacing(2, 3),
}));

const ExpiryContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
  textAlign: 'center',
  animation: `${fadeIn} 0.8s ease-out`,
});

const ExpiryLogo = styled(Box)(({ theme }) => ({
  width: 120,
  height: 120,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  fontSize: '3rem',
  color: 'white',
  boxShadow: '0 8px 32px rgba(238, 90, 82, 0.3)',

  '&::after': {
    content: '"⏰"',
  },
}));

const PasswordSetupPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector(resetLoading);
  const error = useSelector(resetError);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [stringParams, setStringParams] = useState({
    token: '',
    flag: '',
    email: '',
  });
  const [showAlert, setShowAlert] = useState({
    open: false,
    message: '',
    severity: '',
  });

  const registerFlag = stringParams.flag === 'register';
  const resetFlag = stringParams.flag === 'reset';

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paramObj = {};
    for (let [key, value] of params) {
      paramObj[key] = value;
    }
    setStringParams({ ...paramObj });
  }, []);

  useEffect(() => {
    formik.setValues({ email: stringParams.email, token: stringParams.token });
  }, [stringParams]);

  const getExpiryHours = () => {
    if (resetFlag) return '24';
    if (registerFlag) return '168';
    return '2';
  };

  const validationSchema = Yup.object({
    email: Yup.string().email('Invalid Email').required('Email is required'),
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
        'Password must contain at least one uppercase letter, one lowercase letter, one number and one special character'
      )
      .required('Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], 'Passwords must match')
      .required('Confirm Password is required'),
    token: Yup.string().required('Token is invalid'),
  });

  const handleSignInRedirect = () => {
    navigate('/login');
  };

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      confirmPassword: '',
      token: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      const result = await dispatch(resetPassword(values));

      if (result) {
        setShowAlert({
          open: true,
          message: registerFlag ? 'Password set successfully!' : 'Password reset successfully!',
          severity: 'success',
        });
      } else {
        setShowAlert({
          open: true,
          message: error,
          severity: 'error',
        });
      }

      setTimeout(() => {
        if (result) handleSignInRedirect();
      }, 3000);
    },
  });

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  if (error === 'Token has expired') {
    return (
      <PageContainer maxWidth="lg">
        <ExpiryContainer>
          <ExpiryLogo />
          <Typography
            variant="h3"
            fontWeight="bold"
            sx={{
              color: '#2c3e50',
              marginBottom: 2,
              fontSize: { xs: '2rem', sm: '2.8rem' },
            }}
          >
            Your Link Has Been Expired
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b', marginBottom: 1 }}>
            It has been more than {getExpiryHours()} hours since the link was sent.
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b', marginBottom: 3 }}>
            You can request a new link from Sign In page.
          </Typography>
          <PrimaryButton onClick={handleSignInRedirect}>Sign In</PrimaryButton>
        </ExpiryContainer>
      </PageContainer>
    );
  }

  const CustomEmailInputProps = {
    startAdornment: (
      <InputAdornment position="start">
        <Email color="action" />
      </InputAdornment>
    ),
  };

  const CustomPasswordInputProps = {
    startAdornment: (
      <InputAdornment position="start">
        <Lock color="action" />
      </InputAdornment>
    ),
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          onClick={handleTogglePasswordVisibility}
          edge="end"
          size="small"
          sx={{
            color: 'action.active',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  };

  const CustomConfirmPasswordInputProps = {
    startAdornment: (
      <InputAdornment position="start">
        <Lock color="action" />
      </InputAdornment>
    ),
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          onClick={handleToggleConfirmPasswordVisibility}
          edge="end"
          size="small"
          sx={{
            color: 'action.active',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  };

  const handleAlertClose = (reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowAlert((prev) => ({ ...prev, open: false }));
  };

  return (
    <PageContainer maxWidth="lg">
      <Grid2 container justifyContent="center">
        <Grid2 item xs={12} sm={8} md={6} lg={5}>
          <BoxContainer>
            <LogoContainer textAlign="center">
              <StyledLogo src={logo} alt="Rieker Logo" />
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{
                  color: '#2c3e50',
                  marginTop: 1,
                  background: 'linear-gradient(to right, #283048, #859398)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                SIGNS
              </Typography>
            </LogoContainer>

            <Typography
              variant="h5"
              fontWeight="600"
              textAlign="center"
              sx={{
                color: '#2c3e50',
                marginBottom: 3,
              }}
            >
              {registerFlag ? 'Password Setup' : 'Reset Password'}
            </Typography>

            <FormContainer onSubmit={formik.handleSubmit}>
              <AuthTextField
                fullWidth
                label="Email Address"
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                margin="normal"
                disabled
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                slotProps={{ input: CustomEmailInputProps }}
              />

              <AuthTextField
                fullWidth
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                autoComplete="new-password"
                margin="normal"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                slotProps={{ input: CustomPasswordInputProps }}
              />

              <AuthTextField
                fullWidth
                label="Confirm New Password"
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                autoComplete="new-password"
                margin="normal"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                slotProps={{ input: CustomConfirmPasswordInputProps }}
              />

              {formik.touched.token && formik.errors.token && (
                <Alert
                  severity="error"
                  sx={{
                    marginTop: 2,
                    borderRadius: 2,
                    '& .MuiAlert-message': {
                      fontWeight: 500,
                    },
                  }}
                >
                  {formik.errors.token}
                </Alert>
              )}

              {showAlert.open && (
                <Alert
                  severity={showAlert.severity}
                  sx={{
                    marginTop: 2,
                    borderRadius: 2,
                    '& .MuiAlert-message': {
                      fontWeight: 500,
                    },
                  }}
                  onClose={handleAlertClose}
                >
                  {showAlert.message}
                </Alert>
              )}

              <Box mt={4} display="flex" justifyContent="center">
                <PrimaryButton
                  type="submit"
                  variant="contained"
                  disabled={loading || !formik.isValid}
                  sx={{ minWidth: 160 }}
                >
                  {loading ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={20} color="inherit" />
                      <span>{registerFlag ? 'Setting...' : 'Resetting...'}</span>
                    </Box>
                  ) : registerFlag ? (
                    'Set Password'
                  ) : (
                    'Reset Password'
                  )}
                </PrimaryButton>
              </Box>
            </FormContainer>
          </BoxContainer>
        </Grid2>
      </Grid2>
    </PageContainer>
  );
};

export default PasswordSetupPage;
